import { useState } from "react";
import Dialog from "components/Overlays/ResponsiveDialog";
import useOverlayStore from "store/overlayStore";
import useSubmitBook from "api/BookResource/useSubmitBook";
import useFetchCategories from "api/CategoryResource/useFetchCategories";
import useGenres from "api/GenreResource/useGenres";
import useAuthStore from "store/authStore";
import { MAX_BOOK_COVER_MB, MAX_BOOK_PDF_MB, activeCurrencies } from "utils";
import { Formik, Form } from "formik";
import * as Yup from "yup";

import FormField from "components/v2/FormField";
import TagsField from "components/v2/TagsField";
import { IoIosArrowForward } from "react-icons/io";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import RichTextEditor from "components/RichTextEditor";
import { Role } from "utils/server-constants/roles";
import { BookFilesFields, BookFilesUploadModal } from "components/BookUpload";
import {
	getSubmitButtonLabel,
	hasSelectedFiles,
	isActiveRequested,
	isAcceptedBookFile,
} from "api/BookResource/bookFileUpload";

const AddBook = () => {
	const { showCreateBook, setShowCreateBook } = useOverlayStore();
	const [filesModal, setFilesModal] = useState(null);

	const hasRoles = useAuthStore((state) => state.hasRoles);

	const { data: categories, isLoading: isCategoriesLoading } = useFetchCategories("yes");

	const { data: genres, isLoading: isGenresLoading } = useGenres("yes");

	const initialValues = {
		title: "",
		isbn: "",
		author: "",
		publisher_email: "",
		book_cover: null,
		book_file: null,
		price: "",
		zwl_price: "",
		credits: "",
		description: "",
		genres: "",
		tags: [],
		category_id: "",
		subscription: false,
		sellable: false,
		is_active: false,
		isFree: true,
		format: "digital",
		stock_level: "",
		physical_usd_price: "",
		physical_zwl_price: "",
	};

	const validate = Yup.object()
		.shape({
			title: Yup.string().required("Book title is required"),
			author: Yup.string().required("Author is required"),
			publisher_email: !hasRoles([Role.SeniorPublisher, Role.Publisher])
				? Yup.string().required("Publisher's email required").email("Invalid email address")
				: Yup.string().notRequired().email("Invalid email address"),
			price: Yup.number().when("isFree", {
				is: true,
				then: Yup.number().notRequired(),
				otherwise: Yup.number()
					.required("Price is required")
					.moreThan(0, "Price should be greater than 0"),
			}),
			description: Yup.mixed(),
			category_id: Yup.number().required("Book category is required"),
			genres: Yup.string().required("Book genre is required"),
			book_cover: Yup.mixed()
				.nullable()
				.test(
					"fileSize",
					`Cover image size should not exceed ${MAX_BOOK_COVER_MB}MB`,
					(value) => !value || value.size / 1024 / 1024 <= MAX_BOOK_COVER_MB,
				),
			book_file: Yup.mixed()
				.nullable()
				.test(
					"fileSize",
					`PDF size should not exceed ${MAX_BOOK_PDF_MB}MB`,
					(value) => !value || value.size / 1024 / 1024 <= MAX_BOOK_PDF_MB,
				)
				.test("mimeType", "Only PDF documents are allowed", isAcceptedBookFile),
			format: Yup.string().required("Book format is required"),
			stock_level: Yup.mixed().when("format", {
				is: (format) => ["physical", "both"].includes(format),
				then: Yup.number()
					.typeError("Stock level mus be a number")
					.required("Stock level is required"),
				otherwise: Yup.mixed().notRequired(),
			}),
			physical_usd_price: Yup.mixed().when("format", {
				is: (format) => ["physical", "both"].includes(format),
				then: Yup.number()
					.typeError("Stock level mus be a number")
					.required("Set price of the physical book"),
				otherwise: Yup.mixed().notRequired(),
			}),
		})
		.test("activation-files", "Activation requires both a cover and book file", (values) => {
			if (!values?.is_active) return true;
			return Boolean(values?.book_cover && values?.book_file);
		});

	const { mutateAsync, isLoading } = useSubmitBook();

	const onSubmit = async (data) => {
		const payload = {
			...data,
			subscription: data?.subscription ? 1 : 0,
			sellable: data?.sellable ? 1 : 0,
			is_active: data?.is_active ? 1 : 0,
			isFree: data?.isFree ? 1 : 0,
			price: data?.price ?? 0,
		};

		const result = await mutateAsync(payload);
		setShowCreateBook(false);

		if (result?.bookId && result?.wantsActive && !result?.hasFiles) {
			setFilesModal({
				bookId: result.bookId,
				title: data.title,
				wantsActive: true,
			});
		}
	};

	return (
		<>
			<Dialog
				visible={showCreateBook}
				style={{ width: "min(720px, 95vw)" }}
				header={
					<div>
						<p className="text-lg font-semibold text-gray-900">Create book</p>
						<p className="text-sm font-normal text-gray-500 mt-0.5">
							Add book details and optionally upload files in one step.
						</p>
					</div>
				}
				onHide={() => setShowCreateBook(false)}
				position="center"
				draggable={false}
				modal
				contentClassName="rounded-b-xl"
				className="rounded-xl"
			>
				<Formik initialValues={initialValues} validationSchema={validate} onSubmit={onSubmit}>
					{({ setFieldTouched, setFieldValue, values, errors, touched }) => {
						const hasPhysical = ["physical", "both"].includes(values?.format || "");
						const wantsActive = isActiveRequested(values.is_active);
						const hasFiles = hasSelectedFiles(values);
						const activationBlocked = wantsActive && !hasFiles;

						return (
							<Form className="mt-2 space-y-6">
								<div className="space-y-4">
									<h3 className="text-sm font-semibold text-gray-800 border-b pb-2">
										Book details
									</h3>

									<div className="flex gap-2">
										<div className="w-1/2">
											<FormField type="text" name="title" label="Title *" />
										</div>
										<div className="w-1/2">
											<FormField type="text" name="isbn" label="ISBN Number" />
										</div>
									</div>

									<div className="flex gap-2">
										<div
											className={
												hasRoles([Role.SeniorPublisher, Role.Publisher]) ? "w-full" : "w-1/2"
											}
										>
											<FormField type="text" name="author" label="Author Name *" />
										</div>

										{!hasRoles([Role.SeniorPublisher, Role.Publisher]) && (
											<div className="w-1/2">
												<FormField type="text" name="publisher_email" label="Publisher's Email *" />
											</div>
										)}
									</div>

									<div className="flex gap-2">
										<div className="w-1/2">
											<FormField
												type="select"
												name="category_id"
												label="Book category"
												options={
													!isCategoriesLoading &&
													categories?.data?.map((category) => ({
														label: category.name,
														value: category.id,
													}))
												}
											/>
										</div>
										<div className="w-1/2">
											<FormField
												type="select"
												name="genres"
												label="Book Genre"
												options={
													!isGenresLoading &&
													genres?.data?.data?.map((genre) => ({
														label: genre.name,
														value: genre.id,
													}))
												}
											/>
										</div>
									</div>

									<div className="w-full">
										<TagsField name="tags" label="Tags" />
									</div>
								</div>

								<div className="space-y-4">
									<h3 className="text-sm font-semibold text-gray-800 border-b pb-2">Pricing</h3>

									<div className="flex gap-2">
										<div className="w-1/2">
											<FormField
												type="number"
												min="0"
												step="0.01"
												name="price"
												label={`Book Price (${activeCurrencies.USD.label})`}
											/>
										</div>
										<div className="w-1/2">
											<FormField
												type="number"
												min="0"
												step="0.01"
												name="zwl_price"
												label={`Book Price (${activeCurrencies.ZWL.label})`}
											/>
										</div>
									</div>

									<div className={` ${hasPhysical ? "flex gap-2" : "hidden"}`}>
										<div className="w-1/2">
											<FormField
												type="number"
												min="0"
												step="0.01"
												name="physical_usd_price"
												label={`Physical book price (${activeCurrencies.USD.label})`}
											/>
										</div>
										<div className="w-1/2">
											<FormField
												type="number"
												min="0"
												step="0.01"
												name="physical_zwl_price"
												label={`Physical book price (${activeCurrencies.ZWL.label})`}
											/>
										</div>
									</div>
								</div>

								<div className="space-y-2">
									<h3 className="text-sm font-semibold text-gray-800 border-b pb-2">
										Book summary
									</h3>
									<RichTextEditor
										value={values.description}
										onTextChange={(e) => setFieldValue("description", e.htmlValue)}
										error={!!(touched.description && errors.description) && errors.description}
									/>
								</div>

								<div className="space-y-4">
									<h3 className="text-sm font-semibold text-gray-800 border-b pb-2">Book files</h3>
									<BookFilesFields
										values={values}
										errors={errors}
										touched={touched}
										setFieldValue={setFieldValue}
										setFieldTouched={setFieldTouched}
									/>
								</div>

								<div className="space-y-2">
									<h3 className="text-sm font-semibold text-gray-800 border-b pb-2">
										Availability
									</h3>

									<FormField
										type="switcher"
										name="subscription"
										label="Do you want people to subscribe to this book?"
									/>

									<FormField type="switcher" label="Can people buy this book?" name="sellable" />

									<FormField type="switcher" label="Activate the book?" name="is_active" />
									{wantsActive && (
										<p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
											{activationBlocked
												? "Activation requires both a cover and book file in this form."
												: "The book will activate automatically after files finish processing."}
										</p>
									)}

									<FormField type="switcher" label="Make the book free?" name="isFree" />
								</div>

								<div className="flex justify-end border-t border-gray-300 pt-4">
									<button
										type="submit"
										disabled={isLoading}
										className="px-5 py-2 rounded-full bg-akgreenest text-white flex gap-2 items-center justify-center disabled:opacity-70 text-sm font-semibold"
									>
										{getSubmitButtonLabel({ isLoading, mode: "create" })}
										{isLoading ? (
											<AiOutlineLoading3Quarters size={18} className="animate-spin" />
										) : (
											<IoIosArrowForward size={18} />
										)}
									</button>
								</div>
							</Form>
						);
					}}
				</Formik>
			</Dialog>

			{filesModal && (
				<BookFilesUploadModal
					visible={Boolean(filesModal)}
					onHide={() => setFilesModal(null)}
					bookId={filesModal.bookId}
					bookTitle={filesModal.title}
					activateAfterUpload={filesModal.wantsActive}
					allowSkip="create"
				/>
			)}
		</>
	);
};

export default AddBook;
